import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import confetti from 'canvas-confetti';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import './i18n';
import './App.css';

// Import useAuth hook
import { useAuth } from './hooks/useAuth';

// Lucide icons
import { 
  Sparkles, Trophy, BookOpen, Users, Settings, LogOut, Menu, X,
  ArrowRight, CheckCircle, Clock, Star, Brain, Cpu, Database,
  Play, Award, TrendingUp, User, Home, Plus, Upload, Check, Volume2, ArrowLeft,
  CreditCard, QrCode, Wallet, ChevronLeft, ChevronRight
} from 'lucide-react';

// Components
import LanguageSelector from './components/LanguageSelector';
import VoicePlayer from './components/VoicePlayer';

// Pages
import ParentDashboard from './pages/ParentDashboard';
import ParentRegDashboard from './pages/ParentRegDashboard';
import WaitlistLanding from './pages/WaitlistLanding';
import VerifyCert from './pages/VerifyCert';
import AdminCertificates from './pages/AdminCertificates';
import AdminBroadcast from './pages/AdminBroadcast';
import TeacherDashboard from './pages/TeacherDashboard';
import Leaderboard from './pages/Leaderboard';
import Lessons from './pages/Lessons';
import Quiz from './pages/Quiz';
import ProgressPage from './pages/Progress';
import { AdminRoutes } from './pages/Admin';
import IdCard from './pages/IdCard';
import Verify from './pages/Verify';
import LandingTN, { MarketingGallery } from './pages/LandingTN';
import LandingBD from './pages/LandingBD';
import Try from './pages/Try';
import PreviewCertificate from './pages/PreviewCertificate';
import ContinueRegistration from './pages/ContinueRegistration';
import GeoSplash from './components/GeoSplash';

// ShadCN UI Components
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Badge } from './components/ui/badge';
import { Input } from './components/ui/input';
import { Textarea } from './components/ui/textarea';
import { Progress } from './components/ui/progress';
import { Avatar, AvatarFallback } from './components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auth Context - export so it can be used in hooks
export const AuthContext = React.createContext(null);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const { data } = await axios.get(`${API}/me`);
      setUser(data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = (tokenData, userData) => {
    localStorage.setItem('token', tokenData);
    setToken(tokenData);
    setUser(userData);
    axios.defaults.headers.common['Authorization'] = `Bearer ${tokenData}`;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Landing Page
const LandingPage = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  return (
    <div className="min-h-screen watermark-main page-border-main">
      {/* Task V — Brand nav bar (crest + wordmark left, LanguageSelector right) */}
      <nav
        className="w-full flex items-center justify-between px-4 sm:px-8 py-3 border-b border-primary/10 bg-white/70 backdrop-blur-md sticky top-0 z-20"
        data-testid="brand-nav"
      >
        <div className="flex items-center gap-3">
          <img
            src="/brand/crest_flat.png"
            alt="Genius One Plus crest"
            className="h-10 w-auto"
            data-testid="brand-nav-crest"
          />
          <div className="leading-tight">
            <div className="text-lg font-bold text-primary">Genius One Plus</div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
              Scientia Potentia Est
            </div>
          </div>
        </div>
        <LanguageSelector />
      </nav>
      
      {/* Hero Section — painterly split layout with hero-02 (galaxy window) */}
      <div className="hero-gradient min-h-[92vh] flex items-center justify-center px-4 scan-line-effect">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            {/* Left column — copy + CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-left"
            >
              <div className="mb-4 text-5xl">🚀</div>
              <h1 className="text-4xl md:text-6xl font-bold text-primary mb-4 leading-tight">
                {t('hero.title')}
              </h1>
              <p className="text-lg md:text-2xl font-bold text-secondary-foreground mb-3">
                {t('hero.tagline')}
              </p>
              <p className="text-base md:text-lg text-muted-foreground mb-5 max-w-xl">
                {t('hero.subtitle')}
              </p>
              <p className="inline-block mb-6 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-semibold">
                {t('hero.trial.badge')}
              </p>
              <div className="flex gap-3 flex-wrap">
                <Button
                  size="lg"
                  className="h-14 px-8 text-lg button-hover"
                  onClick={() => navigate('/register')}
                  data-testid="primary-cta-start-free-button"
                >
                  {t('hero.cta')} <ArrowRight className="ml-2" />
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="h-14 px-8 text-lg button-hover"
                  data-testid="secondary-cta-sample-lesson"
                >
                  <Link to="/try">{t('hero.cta.secondary')}</Link>
                </Button>
              </div>

              {/* Task V — Accreditation badge inline under the CTAs */}
              <div
                className="mt-6 inline-flex items-center gap-3 px-4 py-2 rounded-lg bg-white/60 border border-primary/20 backdrop-blur-sm"
                data-testid="hero-accreditation-badge"
              >
                <img
                  src="/brand/crest_flat.png"
                  alt=""
                  className="h-10 w-auto"
                  aria-hidden="true"
                />
                <div className="leading-tight">
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">
                    A programme of
                  </div>
                  <div className="text-sm font-semibold text-primary">
                    TEC Sri Lanka Worldwide
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right column — hero-02 painterly illustration + ceremonial crest overlay */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="relative"
              data-testid="hero-illustration"
            >
              <div className="absolute inset-0 bg-primary/5 rounded-3xl transform rotate-2" />
              <img
                src="/marketing/hero-02-boy-galaxy-window.jpeg"
                alt="A curious child pointing at a galaxy through a bedroom window"
                className="relative rounded-3xl shadow-2xl w-full h-auto object-cover"
                loading="eager"
                data-testid="hero-illustration-img"
              />
              {/* Ceremonial velvet crest as a decorative corner emblem */}
              <img
                src="/brand/crest_velvet.jpg"
                alt="Genius One Plus ceremonial crest on burgundy velvet"
                className="absolute -bottom-6 -right-6 w-40 sm:w-52 h-auto rounded-xl shadow-2xl ring-4 ring-white/70 hidden sm:block"
                data-testid="hero-velvet-emblem"
              />
              <p className="mt-3 text-xs text-muted-foreground italic text-center">
                {t('hero.illustration.caption') || 'Every child begins with a question. Genius One Plus turns questions into skills.'}
              </p>
            </motion.div>
          </div>

          {/* Task V — UK Endorsement / About Our Accreditation section */}
          <motion.section
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mt-20 rounded-3xl border border-primary/10 overflow-hidden bg-white/50"
            data-testid="uk-endorsement-section"
          >
            {/* Task V.1 — damask ceremonial banner across the top of the section */}
            <div
              className="w-full h-16 sm:h-24 bg-cover bg-center relative"
              style={{ backgroundImage: "url('/brand/crest_damask.jpg')" }}
              data-testid="endorsement-damask-banner"
              aria-hidden="true"
            >
              {/* subtle navy overlay so the section title below stays legible */}
              <div className="absolute inset-0 bg-primary/20" />
            </div>
            <div className="grid md:grid-cols-2 gap-10 items-center p-6 sm:p-10">
              <div>
                <img
                  src="/brand/brass_plaque.jpg"
                  alt="Brass plaque on mahogany door reading Genius One Plus, Scientia Potentia Est, TEC Sri Lanka Worldwide"
                  className="w-full h-auto rounded-lg shadow-xl"
                  loading="lazy"
                  data-testid="brass-plaque-image"
                />
              </div>
              <div>
                <h3 className="text-2xl md:text-3xl font-bold text-primary mb-3">
                  About TEC Sri Lanka Worldwide
                </h3>
                <p className="text-sm text-muted-foreground italic mb-4">
                  Scientia Potentia Est · Knowledge is Power
                </p>
                <p className="text-base leading-relaxed text-slate-700 mb-3">
                  Genius One Plus is a programme of TEC Sri Lanka Worldwide
                  (Pvt) Ltd, a Colombo-registered educational company. Our
                  curriculum is designed by Sri Lankan educators for Sri Lankan
                  students preparing for the Grade 5-6 transition — priced for
                  local families and delivered on a phone.
                </p>
                <ul className="text-sm text-slate-700 space-y-2" data-testid="endorsement-bullets">
                  <li>✓ Signed certificate PDFs verifiable via public QR + serial</li>
                  <li>✓ Awarded quarterly to the top 10% of students in each subject and grade</li>
                  <li>✓ Physical certificate mailed to the family home on request</li>
                </ul>
              </div>
            </div>
          </motion.section>

          {/* Grade 6 transition subjects — 4 cards (Maths · English · Science · Scratch) */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.35 }}
            className="mt-20 text-center"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-10">
              {t('subjects.section.title')}
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card className="card-shadow text-left" data-testid="subject-card-maths">
                <CardContent className="pt-6">
                  <div className="text-4xl mb-3">📐</div>
                  <h3 className="text-xl font-semibold mb-1">{t('subjects.maths.title')}</h3>
                  <p className="text-sm font-medium text-primary mb-3">{t('subjects.maths.subtitle')}</p>
                  <p className="text-muted-foreground text-sm leading-relaxed">{t('subjects.maths.body')}</p>
                </CardContent>
              </Card>
              <Card className="card-shadow text-left" data-testid="subject-card-english">
                <CardContent className="pt-6">
                  <div className="text-4xl mb-3">📖</div>
                  <h3 className="text-xl font-semibold mb-1">{t('subjects.english.title')}</h3>
                  <p className="text-sm font-medium text-primary mb-3">{t('subjects.english.subtitle')}</p>
                  <p className="text-muted-foreground text-sm leading-relaxed">{t('subjects.english.body')}</p>
                </CardContent>
              </Card>
              <Card className="card-shadow text-left" data-testid="subject-card-science">
                <CardContent className="pt-6">
                  <div className="text-4xl mb-3">🔬</div>
                  <h3 className="text-xl font-semibold mb-1">{t('subjects.science.title')}</h3>
                  <p className="text-sm font-medium text-primary mb-3">{t('subjects.science.subtitle')}</p>
                  <p className="text-muted-foreground text-sm leading-relaxed">{t('subjects.science.body')}</p>
                </CardContent>
              </Card>
              <Card className="card-shadow text-left border-2 border-primary/40 relative" data-testid="subject-card-scratch">
                <Badge className="absolute -top-3 right-4 bg-primary text-primary-foreground">
                  {t('subjects.scratch.badge')}
                </Badge>
                <CardContent className="pt-6">
                  <div className="text-4xl mb-3">🐱</div>
                  <h3 className="text-xl font-semibold mb-1">{t('subjects.scratch.title')}</h3>
                  <p className="text-sm font-medium text-primary mb-3">{t('subjects.scratch.subtitle')}</p>
                  <p className="text-muted-foreground text-sm leading-relaxed">{t('subjects.scratch.body')}</p>
                </CardContent>
              </Card>
            </div>
          </motion.div>

          {/* Story strip — 3 painterly moments (hero-01, hero-03, hero-04) */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mt-20"
            data-testid="story-strip"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
              {t('story.section.title') || 'A programme built around real childhood moments'}
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { src: '/marketing/hero-01-boy-among-stars.jpeg',
                  key: 'story.card.one',
                  fallback: 'When a child dreams big, they need somewhere to point that curiosity.' },
                { src: '/marketing/hero-03-ai-hand-touch.jpeg',
                  key: 'story.card.two',
                  fallback: 'AI is not scary when you meet it the right way — through a fingertip and a smile.' },
                { src: '/marketing/hero-04-two-boys-friendship.jpeg',
                  key: 'story.card.three',
                  fallback: 'Every kid learns better when a friend cheers them on.' },
              ].map((s, i) => (
                <Card key={s.src} className="card-shadow overflow-hidden" data-testid={`story-card-${i + 1}`}>
                  <img
                    src={s.src}
                    alt=""
                    className="w-full h-56 object-cover"
                    loading="lazy"
                  />
                  <CardContent className="pt-5">
                    <p className="text-sm text-muted-foreground italic leading-relaxed">
                      {t(s.key) || s.fallback}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </motion.div>

          {/* Sprint 6/7 — 8-image painting gallery + optional TN nudge */}
          <MarketingGallery />
          {(i18n.language === 'ta' || i18n.language === 'ta-en-dual') && (
            <div className="mt-8 text-center" data-testid="tn-nudge">
              <Link to="/tn" className="inline-block px-5 py-2 rounded-full bg-orange-100 text-orange-900 text-sm font-semibold hover:bg-orange-200">
                தமிழ்நாட்டிற்கு விரைவில் / Coming soon to Tamil Nadu →
              </Link>
            </div>
          )}
          {i18n.language === 'bn' && (
            <div className="mt-8 text-center" data-testid="bd-nudge">
              <Link to="/bd" className="inline-block px-5 py-2 rounded-full bg-emerald-100 text-emerald-900 text-sm font-semibold hover:bg-emerald-200">
                {t('bd.preview.link') || 'শীঘ্রই বাংলাদেশে আসছে →'}
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Pricing — Week 2 Genius One Plus (D4) → Week 4 pivot: LKR 750/mo OR LKR 2000 full programme */}
      <div className="py-20 px-4 bg-white" id="pricing">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-4">{t('pricing.section.title')}</h2>
          <div className="grid md:grid-cols-2 gap-8 mt-10">
            <Card className="card-shadow" data-testid="pricing-card-monthly">
              <CardHeader>
                <Badge className="w-fit mb-2" variant="secondary">{t('pricing.monthly.title')}</Badge>
                <CardTitle className="text-3xl font-bold text-primary mt-2">
                  {t('pricing.monthly.price')}
                </CardTitle>
                <CardDescription className="text-base">{t('pricing.monthly.unit')}</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-6">{t('pricing.monthly.includes')}</p>
                <Button className="w-full button-hover" onClick={() => navigate('/register')}>
                  {t('pricing.cta.trial')}
                </Button>
              </CardContent>
            </Card>
            <Card className="card-shadow border-2 border-primary relative" data-testid="pricing-card-package">
              <Badge className="absolute -top-3 left-4 bg-primary text-primary-foreground">
                {t('pricing.package.recommended')}
              </Badge>
              <CardHeader>
                <Badge className="w-fit mb-2">{t('pricing.package.title')}</Badge>
                <CardTitle className="text-3xl font-bold text-primary mt-2">
                  {t('pricing.package.price')}
                </CardTitle>
                <CardDescription className="text-base">{t('pricing.package.unit')}</CardDescription>
                <p className="text-sm font-semibold text-green-700 mt-2">✓ {t('pricing.package.save')}</p>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-6">{t('pricing.package.includes')}</p>
                <Button className="w-full button-hover" onClick={() => navigate('/register')}>
                  {t('pricing.cta.trial')}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Task V — footer with brand crest */}
      <footer className="w-full border-t border-primary/10 bg-white/60 py-6 px-4 sm:px-8" data-testid="brand-footer">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <img
              src="/brand/crest_flat.png"
              alt=""
              className="h-6 w-auto"
              aria-hidden="true"
              data-testid="brand-footer-crest"
            />
            <span>© Genius One Plus · A programme of TEC Sri Lanka Worldwide (Pvt) Ltd</span>
          </div>
          <div className="italic tracking-widest text-xs uppercase">Scientia Potentia Est</div>
        </div>
      </footer>
    </div>
  );
};

// Auth Pages
// Week 3 (Genius One Plus): register form rewritten for the Sri Lankan
// Grade 5 → Grade 6 transition audience. Sprint 4: submit now hits
// POST /api/register and the form is replaced by one of two post-registration
// screens — FreeOctoberSuccess or BankTransferInstructions — based on the
// server's `next_step` value.
//
// FreeOctoberSuccess — shown when the server returns next_step === "free_october".
// Celebrates the free October promo and offers a WhatsApp reminder CTA.
export const FreeOctoberSuccess = ({ reg, onDone }) => {
  const { t } = useTranslation();
  const waNumber = process.env.REACT_APP_WA_NUMBER || '+94770000000';
  useEffect(() => {
    try { localStorage.setItem('gop_registration_id', reg.registration_id); } catch (e) {}
  }, [reg.registration_id]);
  const waText = encodeURIComponent(
    (t('reg.free.wa.text') || 'Hi, I registered for Genius One Plus (Free October). My ID is:') +
    ' ' + reg.registration_id
  );
  return (
    <div className="min-h-screen hero-gradient flex items-center justify-center px-4 py-10 page-border-main" data-testid="post-reg-free-october">
      <Card className="w-full max-w-lg card-shadow cyan-frame">
        <CardHeader>
          <CardTitle className="text-3xl text-center">🎉 {t('reg.free.title') || 'You are IN for FREE this October!'}</CardTitle>
          <CardDescription className="text-center">
            {t('reg.free.subtitle') || 'Your first month is on us. No payment needed until 01 November.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="rounded-md bg-muted/60 p-4 text-sm space-y-1">
            <div><strong>{t('reg.id.label') || 'Registration ID'}:</strong> <span data-testid="reg-id-value">{reg.registration_id}</span></div>
            <div><strong>{t('reg.status.label') || 'Status'}:</strong> {reg.status}</div>
            {reg.paid_until && (
              <div><strong>{t('reg.paid.until.label') || 'Free access until'}:</strong> {reg.paid_until.slice(0, 10)}</div>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            {t('reg.free.body') || 'We will WhatsApp you the lesson link every morning at 6:00 SLT. Save our number so it does not go to spam.'}
          </p>
          <div className="flex flex-col gap-3">
            <Button asChild className="w-full button-hover" data-testid="wa-reminder-btn">
              <a href={`https://wa.me/${waNumber.replace(/[^\d]/g, '')}?text=${waText}`} target="_blank" rel="noreferrer">
                {t('reg.free.wa.cta') || 'Send WhatsApp reminder'}
              </a>
            </Button>
            <Button variant="outline" className="w-full" onClick={onDone} data-testid="post-reg-done-btn">
              {t('reg.done') || 'Done'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// BankTransferInstructions — shown when the server returns next_step === "bank_transfer".
// Fetches /api/payment/instructions, displays BOC details + QR + amounts,
// and provides a receipt upload → /api/payment/submit-receipt.
export const BankTransferInstructions = ({ reg, onDone }) => {
  const { t, i18n } = useTranslation();
  const [instr, setInstr] = useState(null);
  const [plan, setPlan] = useState('1mo');
  const [receiptFile, setReceiptFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    try { localStorage.setItem('gop_registration_id', reg.registration_id); } catch (e) {}
  }, [reg.registration_id]);

  useEffect(() => {
    const lang = ['en', 'si', 'ta'].includes(i18n.language) ? i18n.language : 'en';
    axios.get(`${API}/payment/instructions?lang=${lang}`)
      .then(r => setInstr(r.data))
      .catch(() => toast.error(t('reg.pay.load.error') || 'Could not load payment details'));
  }, [i18n.language, t]);

  const submitReceipt = async () => {
    if (!receiptFile) {
      toast.error(t('reg.pay.receipt.required') || 'Please attach the receipt image');
      return;
    }
    setSubmitting(true);
    try {
      const b64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result).split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(receiptFile);
      });
      await axios.post(`${API}/payment/submit-receipt`, {
        registration_id: reg.registration_id,
        plan,
        receipt_image_base64: b64,
        paid_at: new Date().toISOString()
      });
      setSubmitted(true);
      // Sprint 10.6 — instant provisional access. Parent gets 48h grace while admin verifies.
      toast.success(t('reg.pay.receipt.provisional_ok') || "🎉 You're in! Full access for 48 hours while we verify your payment.");
    } catch (err) {
      toast.error(err.response?.data?.detail || (t('reg.pay.receipt.error') || 'Upload failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const backendBase = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  return (
    <div className="min-h-screen hero-gradient flex items-center justify-center px-4 py-10 page-border-main" data-testid="post-reg-bank-transfer">
      <Card className="w-full max-w-2xl card-shadow cyan-frame">
        <CardHeader>
          <CardTitle className="text-3xl text-center">{t('reg.pay.title') || 'One last step — Bank of Ceylon transfer'}</CardTitle>
          <CardDescription className="text-center">
            {t('reg.pay.subtitle') || 'Your seat is held. Complete the transfer and upload the receipt.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="rounded-md bg-muted/60 p-4 text-sm space-y-1">
            <div><strong>{t('reg.id.label') || 'Registration ID'}:</strong> <span data-testid="reg-id-value">{reg.registration_id}</span></div>
            <div><strong>{t('reg.pay.ref.label') || 'Reference'}:</strong> GOP-{reg.registration_id}</div>
          </div>

          {instr && (
            <div className="grid md:grid-cols-2 gap-5 items-start">
              <div className="space-y-2 text-sm">
                <div><strong>{t('reg.pay.bank') || 'Bank'}:</strong> {instr.bank}</div>
                <div><strong>{t('reg.pay.account.name') || 'Account name'}:</strong> {instr.account_name}</div>
                <div><strong>{t('reg.pay.account.number') || 'Account number'}:</strong> <span data-testid="acct-number">{instr.account_number}</span></div>
                <div><strong>{t('reg.pay.branch') || 'Branch'}:</strong> {instr.branch}</div>
                <div><strong>{t('reg.pay.wa.number') || 'WhatsApp receipt to'}:</strong> {instr.whatsapp_receipt_number}</div>
                <div className="pt-2">
                  <label className="block text-sm font-medium mb-1">{t('reg.pay.plan') || 'Choose plan'}</label>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => setPlan('1mo')} className={`px-3 py-2 rounded-md border text-sm ${plan==='1mo' ? 'bg-primary text-primary-foreground' : ''}`} data-testid="plan-1mo-btn">
                      {t('reg.pay.plan.1mo') || 'Monthly'} — LKR {instr.amount_lkr}
                    </button>
                    <button type="button" onClick={() => setPlan('3mo')} className={`px-3 py-2 rounded-md border text-sm ${plan==='3mo' ? 'bg-primary text-primary-foreground' : ''}`} data-testid="plan-3mo-btn">
                      {t('reg.pay.plan.3mo') || '3-month'} — LKR {instr.amount_3mo_lkr}
                    </button>
                  </div>
                </div>
              </div>
              <div className="flex flex-col items-center">
                <img src={`${backendBase}${instr.qr_image_url}`} alt="BOC QR" className="w-40 h-40 rounded-md border bg-white p-2" data-testid="boc-qr-img" onError={(e) => { e.currentTarget.style.opacity = '0.3'; }} />
                <p className="text-xs text-muted-foreground mt-2 text-center">{t('reg.pay.qr.hint') || 'Scan with your bank app'}</p>
              </div>
            </div>
          )}

          {instr?.instructions?.length > 0 && (
            <ol className="text-sm space-y-1 list-inside pl-1">
              {instr.instructions.map((line, i) => <li key={i}>{line}</li>)}
            </ol>
          )}

          {/*
            TWL corporate-parent reassurance.  BOC account is registered in
            "TEC Sri Lanka Worldwide (Pvt) Ltd" — this line tells parents in
            advance that this is us so they don't get confused at the money-
            moving moment.  Sourced from i18n so the Sinhala + Tamil readers
            get the same reassurance without a JSX rebuild.
            TODO: FOUNDER TO FILL BOC ACCOUNT NUMBER + BRANCH BEFORE FRIDAY
                  via BOC_ACCOUNT_NUMBER and BOC_BRANCH env vars in
                  backend/.env — right now both default to "TODO_FROM_USER"
                  and will render as-is in the account rows above.
          */}
          <p
            className="text-xs italic text-muted-foreground border-t border-muted pt-3"
            data-testid="twl-reassurance"
          >
            {t('reg.pay.twl.reassurance') ||
              'TEC Sri Lanka Worldwide is our parent company. Genius One Plus is one of its programmes. Your bank receipt will show "TEC SRI LANKA WORLDWIDE (PVT) LTD" as the payee — this is us.'}
          </p>

          {!submitted ? (
            <div className="space-y-2 pt-2">
              <label className="block text-sm font-medium">{t('reg.pay.receipt') || 'Upload receipt (JPG / PNG)'}</label>
              <input type="file" accept="image/*" onChange={(e) => setReceiptFile(e.target.files?.[0] || null)} data-testid="receipt-file-input" className="text-sm" />
              <Button className="w-full button-hover" disabled={submitting || !receiptFile} onClick={submitReceipt} data-testid="submit-receipt-btn">
                {submitting ? (t('reg.pay.submitting') || 'Uploading...') : (t('reg.pay.submit') || 'Submit receipt')}
              </Button>
            </div>
          ) : (
            <div className="rounded-md bg-emerald-50 text-emerald-800 p-3 text-sm" data-testid="receipt-submitted-banner">
              🎉 {t('reg.pay.receipt.provisional_ok') || "You're in! Full access for the next 48 hours while we verify your payment."}
            </div>
          )}

          <Button variant="outline" className="w-full" onClick={onDone} data-testid="post-reg-done-btn">
            {t('reg.done') || 'Done'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

const AuthPage = ({ mode }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login } = useAuth();

  // Login-only state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // Register-only state (9 Grade-6-transition fields)
  const [parentName, setParentName] = useState('');
  const [parentWhatsapp, setParentWhatsapp] = useState('');
  const [studentName, setStudentName] = useState('');
  const [studentGrade, setStudentGrade] = useState('g5');
  const [studentSchool, setStudentSchool] = useState('');
  const [studentDistrict, setStudentDistrict] = useState('');
  const [instructionLang, setInstructionLang] = useState('si');
  const [preferredContact, setPreferredContact] = useState('whatsapp');
  const [regEmail, setRegEmail] = useState('');

  const [loading, setLoading] = useState(false);
  const [postRegState, setPostRegState] = useState(null);

  const SL_DISTRICTS = [
    "Ampara","Anuradhapura","Badulla","Batticaloa","Colombo","Galle",
    "Gampaha","Hambantota","Jaffna","Kalutara","Kandy","Kegalle",
    "Kilinochchi","Kurunegala","Mannar","Matale","Matara","Monaragala",
    "Mullaitivu","Nuwara Eliya","Polonnaruwa","Puttalam","Ratnapura",
    "Trincomalee","Vavuniya"
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (mode === 'register') {
        // +94 WhatsApp — the leading +94 is added on submit; SL numbers = 9 digits after +94
        if (!/^\d{9}$/.test(parentWhatsapp.trim())) {
          toast.error(t('reg.error.whatsapp'));
          setLoading(false);
          return;
        }
        if (!studentDistrict) {
          toast.error(t('reg.error.district') || 'Please select a district');
          setLoading(false);
          return;
        }

        const payload = {
          parent_name: parentName,
          parent_whatsapp: `+94${parentWhatsapp}`,
          student_name: studentName,
          grade: studentGrade === 'g5' ? 5 : 6,
          school: studentSchool,
          district: studentDistrict,
          language: instructionLang,
          preferred_contact: preferredContact,
          email: regEmail || null
        };

        const { data } = await axios.post(`${API}/register`, payload);
        toast.success(t('reg.success'));
        // Task W — stash the full post-reg state + the student_name we just
        // typed so the /register/preview-certificate page can render the
        // aspirational mockup, then bounce the parent into the preview funnel.
        try {
          localStorage.setItem('gop_pending_registration', JSON.stringify({
            ...data,
            student_name: studentName,
            parent_name: parentName,
          }));
        } catch (e) {}
        navigate('/register/preview-certificate');
        return;
      } else {
        const { data } = await axios.post(`${API}/login`, { email, password });
        login(data.access_token, data.user);
        toast.success('Welcome back!');

        // Navigate based on role
        if (data.user.role === 'student') navigate('/dashboard');
        else if (data.user.role === 'parent') navigate('/parent');
        else if (data.user.role === 'teacher') navigate('/teacher');
        else if (data.user.role === 'admin') navigate('/admin');
      }
    } catch (error) {
      const d = error.response?.data?.detail;
      if (error.response?.status === 409 && typeof d === 'object') {
        toast.error(t('reg.error.duplicate') || d.message || 'Already registered');
      } else if (Array.isArray(d)) {
        toast.error(d[0]?.msg || t('reg.error.generic'));
      } else {
        toast.error(d || t('reg.error.generic'));
      }
    } finally {
      setLoading(false);
    }
  };

  const selectClass = "w-full h-10 px-3 rounded-md border border-input bg-background";
  const labelClass = "block text-sm font-medium mb-1 text-foreground";

  // Sprint 4: post-registration screen replaces the form once the server has responded.
  if (mode === 'register' && postRegState) {
    const done = () => { setPostRegState(null); navigate('/'); };
    if (postRegState.next_step === 'free_october') {
      return <FreeOctoberSuccess reg={postRegState} onDone={done} />;
    }
    return <BankTransferInstructions reg={postRegState} onDone={done} />;
  }

  return (
    <div className="min-h-screen hero-gradient flex items-center justify-center px-4 py-10 watermark-sub page-border-main">
      <Card className="w-full max-w-lg card-shadow cyan-frame">
        <CardHeader>
          <CardTitle className="text-3xl text-center" data-testid="auth-title">
            {mode === 'register' ? t('reg.title') : t('auth.welcome')}
          </CardTitle>
          <CardDescription className="text-center" data-testid="auth-subtitle">
            {mode === 'register' ? t('reg.subtitle') : t('auth.loginsubtitle')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="auth-form">
            {mode === 'register' ? (
              <>
                {/* 1 · Parent's Name */}
                <div>
                  <label className={labelClass} htmlFor="reg-parent-name">{t('reg.parent.name')}</label>
                  <Input
                    id="reg-parent-name"
                    value={parentName}
                    onChange={(e) => setParentName(e.target.value)}
                    required
                    data-testid="register-parent-name-input"
                  />
                </div>

                {/* 2 · Parent's WhatsApp */}
                <div>
                  <label className={labelClass} htmlFor="reg-parent-whatsapp">{t('reg.parent.whatsapp')}</label>
                  <div className="flex gap-2">
                    <span className="inline-flex items-center px-3 rounded-md border border-input bg-muted text-sm text-muted-foreground">+94</span>
                    <Input
                      id="reg-parent-whatsapp"
                      inputMode="numeric"
                      pattern="\d{9}"
                      maxLength={10}
                      placeholder="771234567"
                      value={parentWhatsapp}
                      onChange={(e) => setParentWhatsapp(e.target.value.replace(/\D/g, ''))}
                      required
                      data-testid="register-parent-whatsapp-input"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{t('reg.parent.whatsapp.hint')}</p>
                </div>

                {/* 3 · Student's Name */}
                <div>
                  <label className={labelClass} htmlFor="reg-student-name">{t('reg.student.name')}</label>
                  <Input
                    id="reg-student-name"
                    value={studentName}
                    onChange={(e) => setStudentName(e.target.value)}
                    required
                    data-testid="register-student-name-input"
                  />
                </div>

                {/* 4 · Student's Grade */}
                <div>
                  <label className={labelClass} htmlFor="reg-student-grade">{t('reg.student.grade')}</label>
                  <select
                    id="reg-student-grade"
                    className={selectClass}
                    value={studentGrade}
                    onChange={(e) => setStudentGrade(e.target.value)}
                    data-testid="register-student-grade-select"
                  >
                    <option value="g5">{t('reg.student.grade.g5')}</option>
                    <option value="g6">{t('reg.student.grade.g6')}</option>
                  </select>
                </div>

                {/* 5 · School (optional) */}
                <div>
                  <label className={labelClass} htmlFor="reg-student-school">{t('reg.student.school')}</label>
                  <Input
                    id="reg-student-school"
                    value={studentSchool}
                    onChange={(e) => setStudentSchool(e.target.value)}
                    data-testid="register-student-school-input"
                  />
                </div>

                {/* 6 · District (required, Sprint 4) */}
                <div>
                  <label className={labelClass} htmlFor="reg-student-district">{t('reg.student.district')}</label>
                  <select
                    id="reg-student-district"
                    className={selectClass}
                    value={studentDistrict}
                    onChange={(e) => setStudentDistrict(e.target.value)}
                    required
                    data-testid="register-student-district-select"
                  >
                    <option value="">{t('reg.student.district.placeholder') || '— Select district —'}</option>
                    {SL_DISTRICTS.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>

                {/* 7 · Language of Instruction */}
                <div>
                  <label className={labelClass} htmlFor="reg-instruction-lang">{t('reg.language')}</label>
                  <select
                    id="reg-instruction-lang"
                    className={selectClass}
                    value={instructionLang}
                    onChange={(e) => setInstructionLang(e.target.value)}
                    data-testid="register-language-select"
                  >
                    <option value="si">{t('reg.language.si')}</option>
                    <option value="ta">{t('reg.language.ta')}</option>
                    <option value="en">{t('reg.language.en')}</option>
                  </select>
                </div>

                {/* 8 · Preferred Contact */}
                <div>
                  <label className={labelClass} htmlFor="reg-preferred-contact">{t('reg.contact')}</label>
                  <select
                    id="reg-preferred-contact"
                    className={selectClass}
                    value={preferredContact}
                    onChange={(e) => setPreferredContact(e.target.value)}
                    data-testid="register-contact-select"
                  >
                    <option value="whatsapp">{t('reg.contact.whatsapp')}</option>
                    <option value="phone">{t('reg.contact.phone')}</option>
                    <option value="email">{t('reg.contact.email')}</option>
                  </select>
                </div>

                {/* 9 · Email (optional) */}
                <div>
                  <label className={labelClass} htmlFor="reg-email">{t('reg.email')}</label>
                  <Input
                    id="reg-email"
                    type="email"
                    value={regEmail}
                    onChange={(e) => setRegEmail(e.target.value)}
                    data-testid="register-email-input"
                  />
                </div>
              </>
            ) : (
              <>
                <div>
                  <Input
                    type="email"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="auth-email-input"
                  />
                </div>
                <div>
                  <Input
                    type="password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="auth-password-input"
                  />
                </div>
              </>
            )}

            <Button
              type="submit"
              className="w-full button-hover"
              disabled={loading}
              data-testid="auth-submit-button"
            >
              {loading
                ? (mode === 'register' ? t('reg.submitting') : t('loading'))
                : (mode === 'register' ? t('reg.submit') : t('login'))}
            </Button>
          </form>
          <p className="text-center mt-4 text-sm text-muted-foreground">
            {mode === 'register' ? (
              <>{t('reg.have.account')} <Link to="/login" className="text-primary hover:underline" data-testid="auth-login-link">{t('reg.login.link')}</Link></>
            ) : (
              <>{t('auth.noaccount')} <Link to="/register" className="text-primary hover:underline" data-testid="auth-register-link">{t('register')}</Link></>
            )}
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

// Student Dashboard
const StudentDashboard = () => {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const [activities, setActivities] = useState([]);
  const [progress, setProgress] = useState(null);
  const [filter, setFilter] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    fetchActivities();
    fetchProgress();
  }, [filter]);

  const fetchActivities = async () => {
    try {
      const params = {};
      if (filter !== 'all') params.age_group = user.age_group;
      const { data } = await axios.get(`${API}/activities`, { params });
      setActivities(data);
    } catch (error) {
      toast.error('Failed to fetch activities');
    }
  };

  const fetchProgress = async () => {
    try {
      const { data } = await axios.get(`${API}/progress`);
      setProgress(data);
    } catch (error) {
      console.error('Failed to fetch progress');
    }
  };

  const downloadCertificate = async () => {
    try {
      const response = await axios.post(`${API}/certificates/generate`, {}, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `certificate_${user.full_name}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });
      toast.success('Certificate downloaded!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate certificate');
    }
  };

  return (
    <div className="min-h-screen bg-background watermark-main page-border-main">
      {/* Header */}
      <div className="dashboard-gradient border-b dashboard-header-cyan scan-line-effect">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold">{t('welcome')}, {user?.full_name}! 👋</h1>
              <p className="text-muted-foreground">{t('ready')}</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => navigate('/leaderboard')} data-testid="leaderboard-button">
                <Trophy className="mr-2 h-4 w-4" /> Leaderboard
              </Button>
              <LanguageSelector />
              <Button variant="ghost" onClick={logout} data-testid="logout-button">
                <LogOut className="mr-2 h-4 w-4" /> {t('logout')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Progress Stats */}
        {progress && (
          <div className="grid md:grid-cols-4 gap-4 mb-8">
            <Card className="card-shadow">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Activities</p>
                    <p className="text-3xl font-bold text-primary">{progress.activities_completed}</p>
                  </div>
                  <Trophy className="h-10 w-10 text-primary/20" />
                </div>
              </CardContent>
            </Card>
            <Card className="card-shadow">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Points</p>
                    <p className="text-3xl font-bold text-primary">{progress.points || 0}</p>
                  </div>
                  <Star className="h-10 w-10 text-primary/20" />
                </div>
              </CardContent>
            </Card>
            <Card className="card-shadow">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Time Spent</p>
                    <p className="text-3xl font-bold text-primary">{progress.total_time_spent}m</p>
                  </div>
                  <Clock className="h-10 w-10 text-primary/20" />
                </div>
              </CardContent>
            </Card>
            <Card className="card-shadow">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Badges</p>
                    <p className="text-3xl font-bold text-primary">{progress.badges_earned?.length || 0}</p>
                  </div>
                  <Award className="h-10 w-10 text-primary/20" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Certificate Button */}
        {progress && progress.activities_completed >= 5 && (
          <Card className="mb-8 bg-gradient-to-r from-primary/10 to-secondary/20 border-2 border-primary/20">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold mb-1">🎉 Congratulations!</h3>
                  <p className="text-muted-foreground">You've completed {progress.activities_completed} activities! Download your certificate.</p>
                </div>
                <Button onClick={downloadCertificate} data-testid="download-certificate-button">
                  <Award className="mr-2 h-4 w-4" /> Download Certificate
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Activities */}
        <div>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">Activities</h2>
            <Tabs value={filter} onValueChange={setFilter}>
              <TabsList>
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="myage">My Level</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {activities.map((activity) => (
              <Card key={activity.id} className="activity-card cursor-pointer cyan-glow-card" onClick={() => navigate(`/activity/${activity.id}`)}>
                <CardHeader>
                  <div className="flex items-start justify-between mb-2">
                    <Badge variant="secondary">{activity.age_group}</Badge>
                    {activity.is_premium && <Badge variant="destructive">Premium</Badge>}
                  </div>
                  <CardTitle className="line-clamp-2">{activity.title}</CardTitle>
                  <CardDescription className="line-clamp-2">{activity.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span className="flex items-center"><Clock className="mr-1 h-4 w-4" /> {activity.estimated_time}min</span>
                    <span className="capitalize">{activity.difficulty}</span>
                  </div>
                  <Button className="w-full mt-4 button-hover" data-testid={`activity-start-${activity.id}-button`}>
                    Start Activity <Play className="ml-2 h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Pricing Page
const PricingPage = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [qrPage, setQrPage] = useState(1);

  const plans = [
    {
      id: 'foundation_monthly',
      name: 'Foundation Level',
      age: '5-8 years',
      price: '1,200',
      features: ['AI Basics', 'Simple Logic', 'Creative Play', 'Progress Tracking', 'Certificates', 'All Premium Activities']
    },
    {
      id: 'development_monthly',
      name: 'Development Level',
      age: '9-12 years',
      price: '1,800',
      features: ['Logical Reasoning', 'AI Applications', 'Design Thinking', 'Complex Problems', 'Certificates', 'All Premium Activities']
    },
    {
      id: 'mastery_monthly',
      name: 'Mastery Level',
      age: '13-16 years',
      price: '2,800',
      features: ['Advanced AI', 'Innovation Methods', 'Leadership Skills', 'Career Guidance', 'Certificates', 'All Premium Activities']
    }
  ];

  const handleSelectPlan = (planId) => {
    if (!user) {
      navigate('/login');
      return;
    }
    setSelectedPlan(planId);
    setPaymentMethod(null);
    setQrPage(1);
    setShowPaymentDialog(true);
  };

  const handleStripeCheckout = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/subscriptions/checkout?plan_id=${selectedPlan}`);
      window.location.href = data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setLoading(false);
    }
  };

  const handlePayPalCheckout = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/subscriptions/paypal-checkout`, {
        plan_id: selectedPlan
      });
      if (data.approval_url) {
        window.location.href = data.approval_url;
      } else {
        toast.error('PayPal checkout is being configured. Please try QR Code or Card payment.');
        setLoading(false);
      }
    } catch (error) {
      toast.error('PayPal checkout is being configured. Please try QR Code or Card payment.');
      setLoading(false);
    }
  };

  const handleQrPaymentDone = async () => {
    if (!user || !selectedPlan) return;
    setLoading(true);
    try {
      await axios.post(`${API}/subscriptions/qr-payment-notify`, {
        plan_id: selectedPlan,
        payment_method: 'qr_bank_transfer'
      });
      toast.success('Payment notification sent! Your subscription will be activated after verification.');
      setShowPaymentDialog(false);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to notify. Please contact support.');
      setLoading(false);
    }
  };

  const selectedPlanDetails = plans.find(p => p.id === selectedPlan);

  return (
    <div className="min-h-screen bg-background watermark-main page-border-main">
      <div className="dashboard-gradient border-b dashboard-header-cyan scan-line-effect">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold" data-testid="pricing-page-title">Choose Your Plan</h1>
              <p className="text-muted-foreground">Unlock all premium activities and features</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => navigate(user ? '/dashboard' : '/')}>
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
              <LanguageSelector />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="grid md:grid-cols-3 gap-8">
          {plans.map((plan) => (
            <Card key={plan.id} className="card-shadow relative">
              <CardHeader>
                <Badge className="w-fit mb-2">{plan.age}</Badge>
                <CardTitle className="text-2xl">{plan.name}</CardTitle>
                <CardDescription className="text-4xl font-bold text-primary mt-4">
                  LKR {plan.price}
                  <span className="text-sm text-muted-foreground">/month</span>
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button 
                  className="w-full button-hover" 
                  onClick={() => handleSelectPlan(plan.id)}
                  data-testid={`subscribe-${plan.id}-button`}
                >
                  Subscribe Now
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Payment Methods Overview */}
        <div className="mt-12">
          <Card className="max-w-3xl mx-auto">
            <CardContent className="pt-6">
              <h3 className="text-xl font-semibold mb-4 text-center">Available Payment Methods</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="flex flex-col items-center gap-2 p-4 rounded-lg border bg-muted/30">
                  <QrCode className="h-8 w-8 text-primary" />
                  <span className="text-sm font-medium text-center">Bank Transfer (QR)</span>
                </div>
                <div className="flex flex-col items-center gap-2 p-4 rounded-lg border bg-muted/30">
                  <CreditCard className="h-8 w-8 text-primary" />
                  <span className="text-sm font-medium text-center">Card Payment</span>
                </div>
                <div className="flex flex-col items-center gap-2 p-4 rounded-lg border bg-muted/30">
                  <Wallet className="h-8 w-8 text-primary" />
                  <span className="text-sm font-medium text-center">PayPal</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="mt-8">
          <Card className="max-w-2xl mx-auto">
            <CardContent className="pt-6">
              <h3 className="text-xl font-semibold mb-3">What's Included in Premium?</h3>
              <div className="grid md:grid-cols-2 gap-4 text-left">
                <div className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <p className="font-semibold">15 Premium Activities</p>
                    <p className="text-sm text-muted-foreground">Advanced concepts and challenges</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <p className="font-semibold">All 13 Languages</p>
                    <p className="text-sm text-muted-foreground">Learn in your native language</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <p className="font-semibold">Voice Explanations</p>
                    <p className="text-sm text-muted-foreground">Listen to content in any language</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <p className="font-semibold">Certificates</p>
                    <p className="text-sm text-muted-foreground">Download achievement certificates</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Payment Method Selection Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle data-testid="payment-dialog-title">
              {paymentMethod ? (
                <button 
                  onClick={() => setPaymentMethod(null)} 
                  className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-1"
                >
                  <ChevronLeft className="h-4 w-4" /> Back to payment methods
                </button>
              ) : null}
              {!paymentMethod && 'Select Payment Method'}
              {paymentMethod === 'qr' && 'Bank Transfer via QR Code'}
              {paymentMethod === 'card' && 'Card Payment'}
              {paymentMethod === 'paypal' && 'PayPal Payment'}
            </DialogTitle>
            {selectedPlanDetails && !paymentMethod && (
              <DialogDescription>
                <span className="font-semibold text-foreground">{selectedPlanDetails.name}</span> — LKR {selectedPlanDetails.price}/month
              </DialogDescription>
            )}
          </DialogHeader>

          {/* Payment Method Selection */}
          {!paymentMethod && (
            <div className="space-y-3 mt-2">
              <button
                onClick={() => setPaymentMethod('qr')}
                className="w-full flex items-center gap-4 p-4 rounded-lg border-2 border-muted hover:border-primary hover:bg-primary/5 transition-all text-left group"
                data-testid="payment-method-qr"
              >
                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                  <QrCode className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="font-semibold">Bank Transfer (QR Code)</p>
                  <p className="text-sm text-muted-foreground">Scan QR code with your banking app</p>
                </div>
                <ChevronRight className="h-5 w-5 text-muted-foreground ml-auto" />
              </button>

              <button
                onClick={() => setPaymentMethod('card')}
                className="w-full flex items-center gap-4 p-4 rounded-lg border-2 border-muted hover:border-primary hover:bg-primary/5 transition-all text-left group"
                data-testid="payment-method-card"
              >
                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                  <CreditCard className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="font-semibold">Card Payment (Visa / MasterCard)</p>
                  <p className="text-sm text-muted-foreground">Pay securely with your credit or debit card</p>
                </div>
                <ChevronRight className="h-5 w-5 text-muted-foreground ml-auto" />
              </button>

              <button
                onClick={() => setPaymentMethod('paypal')}
                className="w-full flex items-center gap-4 p-4 rounded-lg border-2 border-muted hover:border-primary hover:bg-primary/5 transition-all text-left group"
                data-testid="payment-method-paypal"
              >
                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                  <Wallet className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="font-semibold">PayPal</p>
                  <p className="text-sm text-muted-foreground">Pay with your PayPal account or card via PayPal</p>
                </div>
                <ChevronRight className="h-5 w-5 text-muted-foreground ml-auto" />
              </button>
            </div>
          )}

          {/* QR Code Payment View */}
          {paymentMethod === 'qr' && (
            <div className="space-y-4 mt-2">
              {selectedPlanDetails && (
                <div className="bg-primary/5 rounded-lg p-3 text-center">
                  <p className="text-sm text-muted-foreground">Amount to pay</p>
                  <p className="text-2xl font-bold text-primary">LKR {selectedPlanDetails.price}</p>
                  <p className="text-xs text-muted-foreground">{selectedPlanDetails.name} — Monthly</p>
                </div>
              )}
              
              <div className="bg-white rounded-lg border p-4">
                <p className="text-sm font-medium text-center mb-2 text-muted-foreground">
                  TEC SRI LANKA WORLD WIDE
                </p>
                <p className="text-xs text-center mb-3 text-muted-foreground">
                  Account: 005000189991653
                </p>
                <div className="flex justify-center items-center relative">
                  <img 
                    src={`/qr_code_page_${qrPage}.png`} 
                    alt="Payment QR Code" 
                    className="max-w-[280px] w-full h-auto rounded-md"
                    data-testid="qr-code-image"
                  />
                </div>
                <div className="flex justify-center items-center gap-4 mt-3">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setQrPage(1)}
                    className={qrPage === 1 ? 'border-primary bg-primary/10' : ''}
                  >
                    QR 1
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setQrPage(2)}
                    className={qrPage === 2 ? 'border-primary bg-primary/10' : ''}
                  >
                    QR 2
                  </Button>
                </div>
              </div>

              <div className="bg-muted/50 rounded-lg p-3 text-sm space-y-1">
                <p className="font-medium">Instructions:</p>
                <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                  <li>Open your banking app</li>
                  <li>Scan the QR code above</li>
                  <li>Verify the amount: <span className="font-semibold text-foreground">LKR {selectedPlanDetails?.price}</span></li>
                  <li>Complete the transfer</li>
                  <li>Click "I've Made the Payment" below</li>
                </ol>
              </div>

              <Button 
                className="w-full" 
                onClick={handleQrPaymentDone}
                disabled={loading}
                data-testid="qr-payment-done-button"
              >
                {loading ? 'Notifying...' : "I've Made the Payment"}
              </Button>
              <p className="text-xs text-center text-muted-foreground">
                Your subscription will be activated within 24 hours after payment verification.
              </p>
            </div>
          )}

          {/* Card Payment View */}
          {paymentMethod === 'card' && (
            <div className="space-y-4 mt-2">
              {selectedPlanDetails && (
                <div className="bg-primary/5 rounded-lg p-3 text-center">
                  <p className="text-sm text-muted-foreground">Amount to pay</p>
                  <p className="text-2xl font-bold text-primary">LKR {selectedPlanDetails.price}</p>
                  <p className="text-xs text-muted-foreground">{selectedPlanDetails.name} — Monthly</p>
                </div>
              )}
              <div className="text-center space-y-3">
                <div className="flex justify-center gap-3">
                  <div className="bg-muted/50 rounded-md px-3 py-2 text-xs font-medium">VISA</div>
                  <div className="bg-muted/50 rounded-md px-3 py-2 text-xs font-medium">MasterCard</div>
                  <div className="bg-muted/50 rounded-md px-3 py-2 text-xs font-medium">AMEX</div>
                </div>
                <p className="text-sm text-muted-foreground">
                  You'll be redirected to our secure payment processor (Stripe) to complete your payment.
                </p>
              </div>
              <Button 
                className="w-full" 
                onClick={handleStripeCheckout}
                disabled={loading}
                data-testid="card-payment-button"
              >
                {loading ? 'Redirecting...' : (
                  <>
                    <CreditCard className="mr-2 h-4 w-4" /> Pay with Card — LKR {selectedPlanDetails?.price}
                  </>
                )}
              </Button>
              <p className="text-xs text-center text-muted-foreground">
                Secure payment powered by Stripe. Your card details are never stored on our servers.
              </p>
            </div>
          )}

          {/* PayPal Payment View */}
          {paymentMethod === 'paypal' && (
            <div className="space-y-4 mt-2">
              {selectedPlanDetails && (
                <div className="bg-primary/5 rounded-lg p-3 text-center">
                  <p className="text-sm text-muted-foreground">Amount to pay</p>
                  <p className="text-2xl font-bold text-primary">LKR {selectedPlanDetails.price}</p>
                  <p className="text-xs text-muted-foreground">{selectedPlanDetails.name} — Monthly</p>
                </div>
              )}
              <div className="text-center space-y-3">
                <div className="h-12 w-12 rounded-full bg-[#0070ba]/10 flex items-center justify-center mx-auto">
                  <Wallet className="h-6 w-6 text-[#0070ba]" />
                </div>
                <p className="text-sm text-muted-foreground">
                  Pay securely using your PayPal account or any card through PayPal.
                </p>
              </div>
              <Button 
                className="w-full bg-[#0070ba] hover:bg-[#005ea6] text-white" 
                onClick={handlePayPalCheckout}
                disabled={loading}
                data-testid="paypal-payment-button"
              >
                {loading ? 'Redirecting to PayPal...' : (
                  <>
                    <Wallet className="mr-2 h-4 w-4" /> Pay with PayPal — LKR {selectedPlanDetails?.price}
                  </>
                )}
              </Button>
              <p className="text-xs text-center text-muted-foreground">
                You'll be redirected to PayPal to complete your payment securely.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Subscription Success Page
const SubscriptionSuccess = () => {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const { t } = useTranslation();

  useEffect(() => {
    const checkStatus = async () => {
      const params = new URLSearchParams(window.location.search);
      const sessionId = params.get('session_id');
      const provider = params.get('provider');
      const paypalToken = params.get('token');

      if (provider === 'paypal' && paypalToken) {
        // PayPal return - capture the order
        try {
          await axios.post(`${API}/subscriptions/paypal-capture/${paypalToken}`);
          await new Promise(resolve => setTimeout(resolve, 1500));
        } catch (error) {
          console.error('PayPal capture error:', error);
        }
      } else if (sessionId) {
        // Stripe return
        try {
          await axios.get(`${API}/subscriptions/status/${sessionId}`);
          await new Promise(resolve => setTimeout(resolve, 2000));
        } catch (error) {
          console.error('Status check error:', error);
        }
      }
      setChecking(false);
    };

    checkStatus();
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 to-secondary/10 px-4">
      <Card className="max-w-md w-full">
        <CardContent className="pt-12 pb-8 text-center">
          {checking ? (
            <>
              <div className="text-6xl mb-4 animate-bounce">⏳</div>
              <h2 className="text-2xl font-bold mb-2">Activating Subscription...</h2>
              <p className="text-muted-foreground">Please wait while we confirm your payment</p>
            </>
          ) : (
            <>
              <div className="text-6xl mb-4">🎉</div>
              <h2 className="text-2xl font-bold mb-2 text-primary">Payment Successful!</h2>
              <p className="text-muted-foreground mb-6">
                Your premium subscription is now active. You have access to all premium activities!
              </p>
              <Button className="w-full" onClick={() => navigate('/dashboard')} data-testid="go-to-dashboard-button">
                Go to Dashboard <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Import at the top for language names
const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'ta', name: 'தமிழ்' },
  { code: 'si', name: 'සිංහල' },
  { code: 'hi', name: 'हिन्दी' },
  { code: 'ms', name: 'Bahasa Melayu' },
  { code: 'tl', name: 'Filipino' },
  { code: 'zh', name: '简体中文' },
  { code: 'th', name: 'ไทย' },
  { code: 'ur', name: 'اردو' },
  { code: 'bn', name: 'বাংলা' },
  { code: 'zh-TW', name: '繁體中文' },
  { code: 'ko', name: '한국어' },
  { code: 'ja', name: '日本語' }
];

// Activity Detail Page
const ActivityDetail = () => {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const [activity, setActivity] = useState(null);
  const [activityEnglish, setActivityEnglish] = useState(null);
  const [completed, setCompleted] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchActivity();
  }, [id, i18n.language]); // Refetch when language changes

  const fetchActivity = async () => {
    try {
      // Fetch with language parameter for translation
      const { data } = await axios.get(`${API}/activities/${id}?lang=${i18n.language}`);
      setActivity(data);
      
      // Also fetch English version for bilingual voice
      if (i18n.language !== 'en') {
        const { data: enData } = await axios.get(`${API}/activities/${id}?lang=en`);
        setActivityEnglish(enData);
      } else {
        setActivityEnglish(data);
      }
    } catch (error) {
      toast.error('Activity not found');
      navigate('/dashboard');
    }
  };

  const markComplete = async () => {
    try {
      await axios.post(`${API}/completions?activity_id=${activity.id}&time_spent_minutes=15`);
      setCompleted(true);
      confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } });
      toast.success('Activity completed! +10 points');
      setTimeout(() => navigate('/dashboard'), 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark complete');
    }
  };

  if (!activity) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;

  return (
    <div className="min-h-screen bg-background watermark-sub page-border-main">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-6">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Activities
        </Button>

        <Card className="card-shadow cyan-glow-card">
          <CardHeader>
            <div className="flex gap-2 mb-4">
              <Badge>{activity.age_group}</Badge>
              <Badge variant="secondary">{activity.topic.replace('_', ' ')}</Badge>
              {activity.is_premium && <Badge variant="destructive">Premium</Badge>}
            </div>
            <CardTitle className="text-4xl">{activity.title}</CardTitle>
            <CardDescription className="text-lg">{activity.description}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {activity.image_url && (
              <img src={activity.image_url} alt={activity.title} className="w-full rounded-lg" />
            )}

            {/* Topic Explanation */}
            {activity.topic_explanation_title && (
              <div className="bg-primary/5 border-2 border-primary/20 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-2xl font-bold text-primary flex items-center gap-2">
                    📚 {activity.topic_explanation_title}
                  </h3>
                  <VoicePlayer 
                    text={activityEnglish?.topic_explanation || activity.topic_explanation} 
                    translatedText={activity.topic_explanation} 
                  />
                </div>
                <p className="text-base leading-relaxed whitespace-pre-line">
                  {activity.topic_explanation}
                </p>
              </div>
            )}

            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xl font-semibold">📋 {t('activity.instructions')}</h3>
                <VoicePlayer 
                  text={activityEnglish?.instructions?.join('. ') || activity.instructions.join('. ')} 
                  translatedText={activity.instructions.join('. ')} 
                />
              </div>
              <ol className="space-y-2">
                {activity.instructions.map((instruction, index) => (
                  <li key={index} className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm">
                      {index + 1}
                    </span>
                    <span>{instruction}</span>
                  </li>
                ))}
              </ol>
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xl font-semibold">🎯 {t('activity.objectives')}</h3>
                <VoicePlayer 
                  text={activityEnglish?.learning_objectives?.join('. ') || activity.learning_objectives.join('. ')} 
                  translatedText={activity.learning_objectives.join('. ')} 
                />
              </div>
              <ul className="space-y-2">
                {activity.learning_objectives.map((objective, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                    <span>{objective}</span>
                  </li>
                ))}
              </ul>
            </div>

            {!completed ? (
              <Button 
                className="w-full h-14 text-lg button-hover" 
                onClick={markComplete}
                data-testid="activity-player-mark-complete-button"
              >
                <Check className="mr-2" /> Mark as Complete
              </Button>
            ) : (
              <div className="text-center py-8">
                <div className="text-6xl mb-4">🎉</div>
                <h3 className="text-2xl font-bold text-primary mb-2">Activity Completed!</h3>
                <p className="text-muted-foreground">Great job! Redirecting to dashboard...</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Sprint 11 (Task J) — Toggles `<html dir>` between LTR and RTL based on the
// active i18n language. Urdu (`ur`) is the only RTL locale we ship today; if
// we add Arabic later, extend RTL_LANGS. English admin surfaces stay LTR
// automatically because they explicitly force `en` on mount.
const RTL_LANGS = new Set(['ur']);
function RTLManager() {
  const { i18n } = useTranslation();
  React.useEffect(() => {
    const apply = (lng) => {
      const short = (lng || 'en').split(/[-_@]/)[0].toLowerCase();
      const isRtl = RTL_LANGS.has(short);
      document.documentElement.dir = isRtl ? 'rtl' : 'ltr';
      document.documentElement.lang = lng || 'en';
    };
    apply(i18n.language);
    i18n.on('languageChanged', apply);
    return () => i18n.off('languageChanged', apply);
  }, [i18n]);
  return null;
}

// Main App
function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <RTLManager />
        <GeoSplash />
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/register" element={<AuthPage mode="register" />} />
          <Route path="/register/preview-certificate" element={<PreviewCertificate />} />
          <Route path="/register/continue" element={<ContinueRegistration />} />
          <Route path="/login" element={<AuthPage mode="login" />} />
          <Route path="/dashboard" element={<ProtectedRoute><StudentDashboard /></ProtectedRoute>} />
          <Route path="/activity/:id" element={<ProtectedRoute><ActivityDetail /></ProtectedRoute>} />
          <Route path="/parent" element={<ProtectedRoute><ParentDashboard /></ProtectedRoute>} />
          {/* Sprint 10.7 — WhatsApp-PIN parent dashboard (no auth) */}
          <Route path="/parent/:registration_id" element={<ParentRegDashboard />} />
          {/* Sprint 12 — Certificate verify (public) + admin cert page (JWT-gated internally) */}
          <Route path="/verify-cert/:serial" element={<VerifyCert />} />
          <Route path="/admin/certificates" element={<AdminCertificates />} />
          {/* Sprint N — Admin broadcast composer (JWT-gated internally) */}
          <Route path="/admin/broadcast" element={<AdminBroadcast />} />
          <Route path="/teacher" element={<ProtectedRoute><TeacherDashboard /></ProtectedRoute>} />
          <Route path="/leaderboard" element={<ProtectedRoute><Leaderboard /></ProtectedRoute>} />
          {/* Sprint 5 — public routes gated by localStorage registration_id */}
          <Route path="/lessons" element={<Lessons />} />
          <Route path="/quiz/:lesson_id" element={<Quiz />} />
          <Route path="/progress/:registration_id" element={<ProgressPage />} />
          {/* Sprint 6 — Admin panel (nested routes handled inside AdminRoutes) */}
          <Route path="/admin/*" element={<AdminRoutes />} />
          <Route path="/tn" element={<LandingTN />} />
          <Route path="/bd" element={<LandingBD />} />
          {/* Sprint 10.9 — market expansion waitlist pages (unified Coming 2027) */}
          <Route path="/my" element={<WaitlistLanding slug="my" />} />
          <Route path="/in-hi" element={<WaitlistLanding slug="in-hi" />} />
          <Route path="/id" element={<WaitlistLanding slug="id" />} />
          <Route path="/th" element={<WaitlistLanding slug="th" />} />
          <Route path="/pk" element={<WaitlistLanding slug="pk" />} />
          <Route path="/hk" element={<WaitlistLanding slug="hk" />} />
          <Route path="/try" element={<Try />} />
          <Route path="/id-card/:registration_id" element={<IdCard />} />
          <Route path="/verify/:serial" element={<Verify />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/subscription-success" element={<ProtectedRoute><SubscriptionSuccess /></ProtectedRoute>} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">🎓</div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  return children;
};

export default App;
